import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ToastProvider } from "./hooks/useToast";
import ToastContainer from "./components/ToastContainer";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { api } from "./lib/api";
import PWAInstallBanner from "./components/PWAInstallBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageSkeleton, DashboardSkeleton } from "./components/SkeletonLoader";
import NetworkBanner from "./components/NetworkBanner";
import NotFound from "./pages/NotFound";

// Eagerly loaded: Landing (above the fold) + auth pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Offline from "./pages/Offline";
import { useTranslation } from 'react-i18next';

// Lazy loaded: non-critical UI (deferred to idle time)
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

// Lazy loaded: Pricing (heavy, not above-the-fold)
import Pricing from "./pages/Pricing";

// Lazy loaded: heavy / below-the-fold pages
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import MaxOSCloudPreview from "./pages/MaxOSCloudPreview";
import Marketplace from "./pages/Marketplace";
import CustomerSupport from "./pages/CustomerSupport";
import Consultancy from "./pages/Consultancy";
import RealEstate from "./pages/RealEstate";
import MarketingAgencies from "./pages/MarketingAgencies";
import Ecommerce from "./pages/Ecommerce";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogCategory from "./pages/BlogCategory";
import BlogTag from "./pages/BlogTag";
import AuthorPage from "./pages/AuthorPage";
import Cases from "./pages/Cases";
import CaseStudy from "./pages/CaseStudy";
import FAQ from "./pages/FAQ";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import VsZapier from "./pages/VsZapier";
import VsChatGPT from "./pages/VsChatGPT";
import VsMake from "./pages/VsMake";
import Comparison from "./pages/Comparison";

// Lazy loaded: dashboard area (heavy, authenticated)
import Dashboard from "./pages/Dashboard";
import Support from "./pages/Support";
import Course from "./pages/Course";
import CourseLesson from "./pages/CourseLesson";
import Admin from "./pages/Admin";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Referral from "./pages/Referral";
import ReferralLanding from "./components/ReferralLanding";
import AnalyticsDashboard from "./components/AnalyticsDashboard";

// Lazy loaded: feature pages
import ApiDocs from "./pages/ApiDocs";
import IntegrationsPage from "./pages/IntegrationsPage";
import IntegrationDetailRoute from "./pages/IntegrationDetailRoute";
import AbTestingPage from "./pages/AbTestingPage";
import AutomationsPage from "./pages/AutomationsPage";
import AbTestingDemo from "./pages/AbTestingDemo";
import AutomationsDemo from "./pages/AutomationsDemo";
import Forum from "./pages/Forum";
import ForumTopic from "./pages/ForumTopic";
import HelpCenter from "./pages/HelpCenter";
import DeployPage from "./pages/DeployPage";
import Newsletter from "./pages/Newsletter";
import SkillsPage from "./pages/SkillsPage";
import CreateSkillPage from "./pages/CreateSkillPage";
import SkillDetailPage from "./pages/SkillDetailPage";
import VerifyCreatorPage from "./pages/VerifyCreatorPage";
import VersionHistory from "./components/VersionHistory";
import CreatorProfile from "./components/CreatorProfile";
import AuditPanel from "./components/AuditPanel";
import OnboardingWizard from "./components/OnboardingWizard";

// Lazy loaded: floating widgets (non-critical)
import FloatingSalesChat from "./components/FloatingSalesChat";
import ExitIntentPopup from "./components/ExitIntentPopup";
import InteractiveDemo from "./components/InteractiveDemo";
import SocialProofNotifications from "./components/SocialProofNotifications";
import NotificationPrompt from "./components/NotificationPrompt";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const location = window.location;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">{t('auth.authenticating')}</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = m[1];
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState({}, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login?error=google", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">{t('auth.finalizing_session')}</div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/products" element={<Navigate to="/pricing" replace />} />
      <Route path="/products/:productId" element={<ErrorBoundary><ProductDetail /></ErrorBoundary>} />
      <Route path="/preview/max-os-cloud" element={<ErrorBoundary><MaxOSCloudPreview /></ErrorBoundary>} />
      <Route path="/pricing" element={<ErrorBoundary><Pricing /></ErrorBoundary>} />
      <Route path="/marketplace" element={<ErrorBoundary><Marketplace /></ErrorBoundary>} />
      <Route path="/marketplace/customer-support" element={<ErrorBoundary><CustomerSupport /></ErrorBoundary>} />
      <Route path="/marketplace/consultancy" element={<ErrorBoundary><Consultancy /></ErrorBoundary>} />
      <Route path="/marketplace/real-estate" element={<ErrorBoundary><RealEstate /></ErrorBoundary>} />
      <Route path="/marketplace/marketing-agencies" element={<ErrorBoundary><MarketingAgencies /></ErrorBoundary>} />
      <Route path="/marketplace/ecommerce" element={<ErrorBoundary><Ecommerce /></ErrorBoundary>} />
      <Route path="/marketplace/:verticalId" element={<ErrorBoundary><Marketplace /></ErrorBoundary>} />
      <Route path="/contact" element={<ErrorBoundary><Contact /></ErrorBoundary>} />
      <Route path="/blog" element={<ErrorBoundary><Blog /></ErrorBoundary>} />
      <Route path="/blog/category/:category" element={<ErrorBoundary><BlogCategory /></ErrorBoundary>} />
      <Route path="/blog/tag/:tag" element={<ErrorBoundary><BlogTag /></ErrorBoundary>} />
      <Route path="/blog/author/:name" element={<ErrorBoundary><AuthorPage /></ErrorBoundary>} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/cases" element={<ErrorBoundary><Cases /></ErrorBoundary>} />
      <Route path="/cases/:slug" element={<ErrorBoundary><CaseStudy /></ErrorBoundary>} />
      <Route path="/faq" element={<ErrorBoundary><FAQ /></ErrorBoundary>} />
      <Route path="/terms" element={<ErrorBoundary><Terms /></ErrorBoundary>} />
      <Route path="/privacy" element={<ErrorBoundary><Privacy /></ErrorBoundary>} />
      <Route path="/vs/zapier" element={<ErrorBoundary><VsZapier /></ErrorBoundary>} />
      <Route path="/vs/chatgpt" element={<ErrorBoundary><VsChatGPT /></ErrorBoundary>} />
      <Route path="/vs/make" element={<ErrorBoundary><VsMake /></ErrorBoundary>} />
      <Route path="/compare/:competitor" element={<ErrorBoundary><Comparison /></ErrorBoundary>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary><Dashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><ErrorBoundary><Support /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/dashboard/course" element={<ProtectedRoute><ErrorBoundary><Course /></ErrorBoundary></ProtectedRoute>}>
        <Route path=":lessonId" element={<ErrorBoundary><CourseLesson /></ErrorBoundary>} />
      </Route>
      <Route path="/checkout" element={<ErrorBoundary><CheckoutPage /></ErrorBoundary>} />
      <Route path="/checkout/success" element={<ProtectedRoute><ErrorBoundary><CheckoutSuccess /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><ErrorBoundary><Admin /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute><ErrorBoundary><Referral /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/ref/:code" element={<ErrorBoundary><ReferralLanding /></ErrorBoundary>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><ErrorBoundary><AnalyticsDashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/docs/api" element={<ErrorBoundary><ApiDocs /></ErrorBoundary>} />
      <Route path="/marketplace/integrations" element={<ErrorBoundary><IntegrationsPage /></ErrorBoundary>} />
      <Route path="/marketplace/integrations/:id" element={<ErrorBoundary><IntegrationDetailRoute /></ErrorBoundary>} />
      <Route path="/dashboard/ab-testing" element={<ProtectedRoute><ErrorBoundary><AbTestingPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/dashboard/automations" element={<ProtectedRoute><ErrorBoundary><AutomationsPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/demo/ab-testing" element={<ErrorBoundary><AbTestingDemo /></ErrorBoundary>} />
      <Route path="/demo/automations" element={<ErrorBoundary><AutomationsDemo /></ErrorBoundary>} />
      <Route path="/community" element={<ErrorBoundary><Forum /></ErrorBoundary>} />
      <Route path="/community/:category" element={<ErrorBoundary><Forum /></ErrorBoundary>} />
      <Route path="/community/topic/:topicId" element={<ErrorBoundary><ForumTopic /></ErrorBoundary>} />
      <Route path="/community/new" element={<ErrorBoundary><Forum /></ErrorBoundary>} />
      <Route path="/help" element={<ErrorBoundary><HelpCenter /></ErrorBoundary>} />
      <Route path="/deploy" element={<ProtectedRoute><ErrorBoundary><DeployPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/newsletter" element={<ErrorBoundary><Newsletter /></ErrorBoundary>} />
      <Route path="/skills" element={<ErrorBoundary><SkillsPage /></ErrorBoundary>} />
      <Route path="/skills/create" element={<ErrorBoundary><CreateSkillPage /></ErrorBoundary>} />
      <Route path="/skills/:skillSlug" element={<ErrorBoundary><SkillDetailPage /></ErrorBoundary>} />
      <Route path="/skills/creator/:userId" element={<ErrorBoundary><CreatorProfile /></ErrorBoundary>} />
      <Route path="/skills/creator/verify" element={<ErrorBoundary><VerifyCreatorPage /></ErrorBoundary>} />
      <Route path="/skills/:id/versions" element={<ErrorBoundary><VersionHistory /></ErrorBoundary>} />
      <Route path="/admin/skills" element={<ErrorBoundary><AuditPanel /></ErrorBoundary>} />
      <Route path="/onboarding" element={<ErrorBoundary><OnboardingWizard /></ErrorBoundary>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    // Service Worker registration removed to prevent cross-domain cache issues
    // The SW from sharkxelectric.com was intercepting requests on staging.maxemployee.com
  }, []);

  return (
    <HelmetProvider>
        <div className="App" lang="en">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-amber-500 focus:text-black focus:px-4 focus:py-2 focus:font-mono focus:text-sm focus:uppercase focus:tracking-wider"
          >
            Skip to main content
          </a>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <NetworkBanner />
                <AppRouter />
                <FloatingSalesChat />
                <ExitIntentPopup />
                <InteractiveDemo />
                <SocialProofNotifications />
                <NotificationPrompt />
                <PWAInstallBanner installPromptEvent={installPrompt} />
                <Toaster theme="dark" position="top-right" aria-live="polite" />
                <ToastContainer />
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </div>
      </HelmetProvider>
  );
}

export default App;
